import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import api from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { Reveal } from "@/components/motion";

const PaymentSuccess = () => {
  useSeo({ title: "Paiement", noindex: true });
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [state, setState] = useState("checking");
  const [lastOrder, setLastOrder] = useState(null);

  useEffect(() => {
    try {
      setLastOrder(JSON.parse(localStorage.getItem("sk_last_order")));
    } catch {}
  }, []);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let cancelled = false;
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          if (!cancelled) setState("paid");
          return;
        }
      } catch {}
      if (!cancelled && ++tries < 10) setTimeout(poll, 2000);
      else if (!cancelled) setState("timeout");
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return (
    <main data-testid="payment-success-page" className="">
      <div className="mx-auto flex max-w-2xl flex-col items-center px-6 py-28 text-center lg:px-10">
        {state === "checking" && (
          <Reveal>
            <Loader2 data-testid="payment-verifying-spinner" className="h-14 w-14 animate-spin text-terracotta" />
            <h1 className="mt-6 font-display text-3xl font-semibold text-bark sm:text-4xl">Vérification du paiement…</h1>
            <p className="mt-3 text-sm text-bark/50">Nous confirmons votre paiement auprès de Stripe. Quelques secondes suffisent.</p>
          </Reveal>
        )}
        {state === "paid" && (
          <Reveal>
            <CheckCircle2 data-testid="payment-success-icon" className="mx-auto h-16 w-16 text-terracotta" />
            <h1 className="mt-6 font-display text-4xl font-bold tracking-tight text-bark sm:text-5xl">Paiement confirmé</h1>
            <p className="mt-4 text-bark/60">
              Jërëjëf ! Votre paiement par carte a bien été reçu.
              {lastOrder?.orderNumber && (
                <>
                  {" "}Votre numéro de commande :{" "}
                  <span data-testid="payment-success-order-number" className="font-bold text-terracotta">{lastOrder.orderNumber}</span>
                </>
              )}
            </p>
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <Link to="/suivi" data-testid="payment-success-tracking-link" className="flex h-14 items-center rounded-full bg-terracotta px-8 text-sm font-bold uppercase tracking-[0.15em] text-sand transition-colors hover:bg-terracotta-dark">
                Suivre ma commande
              </Link>
              <Link to="/boutique" data-testid="payment-success-shop-link" className="flex h-14 items-center rounded-full border border-bark/20 px-8 text-sm font-bold uppercase tracking-[0.15em] text-bark transition-colors hover:border-terracotta hover:text-terracotta">
                Retour à la boutique
              </Link>
            </div>
          </Reveal>
        )}
        {(state === "timeout" || state === "error") && (
          <Reveal>
            <XCircle data-testid="payment-error-icon" className="mx-auto h-16 w-16 text-ochre" />
            <h1 className="mt-6 font-display text-3xl font-semibold text-bark sm:text-4xl">Confirmation en attente</h1>
            <p className="mt-4 text-bark/60">
              La confirmation prend plus de temps que prévu. Si le montant a été débité, votre commande sera validée automatiquement.
              {lastOrder?.orderNumber && (
                <>
                  {" "}Suivez-la avec le numéro <span className="font-bold text-terracotta">{lastOrder.orderNumber}</span>.
                </>
              )}
            </p>
            <Link to="/suivi" data-testid="payment-timeout-tracking-link" className="mt-10 inline-flex h-14 items-center rounded-full bg-terracotta px-8 text-sm font-bold uppercase tracking-[0.15em] text-sand transition-colors hover:bg-terracotta-dark">
              Suivre ma commande
            </Link>
          </Reveal>
        )}
      </div>
    </main>
  );
};

export default PaymentSuccess;
